import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { MuiThemeProvider } from '@material-ui/core/styles';
import get from 'lodash/get';
import includes from 'lodash/includes';
import toLower from 'lodash/toLower';

import FormWizard from 'Components/form-wizard/form-wizard';
import Modal from 'Components/modals/modal/modal';
import CreateServiceLoader from 'Components/signup-followup-details/create-service-loader';
import WizardNavigation from 'Components/wizard-navigation/wizard-navigation';
import useMobileDetect from 'Hooks/useMobileDetect';
import usePrevious from 'Hooks/usePrevious';
import useWindowSize from 'Hooks/useWindowSize';
import totangoTheme from 'Theme/totangoTheme';
import { isFileExists } from 'Utils/api/uploads';
import track from 'Utils/tracking';
import { isLocal, isTestEnv, uploadcareUrl } from 'Utils/utils';

import AccountSettings from './steps/account-settings';
import CheckYourEmail from './steps/check-your-email';
import CreateAccount from './steps/create-account';
import SetUserDetails from './steps/set-user-details';
import SignupError from './steps/signup-error';
import { gaPushEvent, SIGNUP_METHOD_TYPES } from './tracking/google-analytics';
import {
	createAccount,
	ERROR_TYPE_MAP,
	generateAuthenticationCode as generateAuthenticationCodeApi,
	trackContact,
	waitForProgress,
} from './utils/api';
import {
	BACKGROUND_UPLOADCARE_OPTIONS,
	dataCenters,
	DEFAULT_SIGNUP_BACKGROUND_ID,
} from './utils/constants';
import { STEPS } from './utils/steps-utils';
import {
	currentSite,
	DEFAULT_ROUTE,
	DEFAULT_SIGNUP_EDITION,
	trackExternal,
	uploadToUploadcareFromUrl,
	utmParams,
} from './utils/utils';
import MarketingIframe from './marketing-iframe';

import './signup-page.less';

const { searchParams } = new URL(window.location.href);
const isShortSignup = searchParams.get('short_signup') !== null;
const signupCode = searchParams.get('signup_code');
const signupEmail = searchParams.get('signup_email');
const signupAccountName = searchParams.get('account_name');
const edition = DEFAULT_SIGNUP_EDITION;

const firstNameParam = searchParams.get('first_name');
const lastNameParam = searchParams.get('last_name');
const jobTitleParam = searchParams.get('job_title');
const companyCityParam = searchParams.get('company_city');
const companyStateParam = searchParams.get('company_state');
const companyCountryParam = searchParams.get('company_country');

const companyDescriptionParam = searchParams.get('company_description');
const numberOfAccountsParam = searchParams.get('number_of_accounts');
const employeeNumberParam = searchParams.get('employee_number');
const oauthIssuerParam = searchParams.get('oauth_issuer');
const passwordParam = searchParams.get('password');
const EU_OFFSETS = [0, -60, -120, -180];
const isEuTimeZone = EU_OFFSETS.includes(new Date().getTimezoneOffset());
const shouldShowDataCenterOptions =
	!isTestEnv() && !isLocal() && (searchParams.get('show_data_centers') !== null || isEuTimeZone);
const chosenDataCenter = includes(window.location.href, '/t11/') ? 'eu' : 'us';
const backGroundPicture = searchParams.get('background');

const doTrackContactDummy = async ({
	trimmedEmail,
	employeeNumber,
	firstName,
	lastName,
	jobTitle,
	companyCity,
	companyState,
	companyCountry,
	activeStep,
	isCompleted,
	isSuccess,
	agreedReceiveContent,
	serviceId,
}) => {
	const emailDomain = trimmedEmail.split('@')[1];
	const company = emailDomain && emailDomain.split('.')[0];
	let employeeNumberToSend;

	switch (employeeNumber) {
		case '50':
			employeeNumberToSend = '1-50';
			break;
		case '100':
			employeeNumberToSend = '51-100';
			break;
		case '200':
			employeeNumberToSend = '101-200';
			break;
		case '500':
			employeeNumberToSend = '201-500';
			break;
		case '1000':
			employeeNumberToSend = '501-1000';
			break;
		case '5000':
			employeeNumberToSend = '1001-5000';
			break;
		case '10000':
			employeeNumberToSend = '5001-10000';
			break;
		case '50000':
			employeeNumberToSend = '50000';
			break;
		default:
	}

	const params = {
		email: trimmedEmail,
		firstname: firstName && firstName.length > 0 ? firstName : undefined,
		lastname: lastName && lastName.length > 0 ? lastName : undefined,
		jobtitle: jobTitle && jobTitle.length > 0 ? jobTitle : undefined,
		company_city: companyCity && companyCity.length > 0 ? companyCity : undefined,
		company_state: companyState && companyState.length > 0 ? companyState : undefined,
		company_country: companyCountry && companyCountry.length > 0 ? companyCountry : undefined,
		company_size__c:
			employeeNumberToSend && employeeNumberToSend.length > 0 ? employeeNumberToSend : undefined,
		company: company && company.length > 0 ? company : undefined,
		service_id__c: serviceId && serviceId.length > 0 ? serviceId : undefined,
		user_confirmed_email: (activeStep > 1).toString(),
		password_set: (activeStep > 2).toString(),
		entered_personal_details: (activeStep > 3).toString(),
		entered_account_details: (activeStep > 4 || isCompleted || isSuccess).toString(),
	};
	if (agreedReceiveContent) {
		params.hs_legal_basis = 'Freely given consent from contact';
	}

	try {
		await trackContact(params);
	} catch (err) {
		console.error('tracking err', err);
	}
};

export default function SignupPage() {
	const history = useHistory();
	const { isMobile } = useMobileDetect();

	const [activeStep, setActiveStep] = useState(signupCode ? 4 : 0);
	const [isLoading, setIsLoading] = useState(false);
	const [isError, setIsError] = useState(false);
	const [errorMessage, setErrorMessage] = useState(null);
	const [isSuccess, setIsSuccess] = useState(false);
	const [forceUpdateHeight, setForceUpdateHeight] = useState();

	const [email, setEmail] = useState(signupEmail || '');
	const trimmedEmail = email.trim();

	const [password, setPassword] = useState(passwordParam || '');
	const [oauthIssuer, setOauthIssuer] = useState(oauthIssuerParam);
	const [firstName, setFirstName] = useState(firstNameParam || '');
	const [lastName, setLastName] = useState(lastNameParam || '');
	const [profileImageUrl, setProfileImageUrl] = useState('');
	const [emailHash, setEmailHash] = useState('');
	const [avatarId, setAvatarId] = useState('');
	const [jobTitle, setJobTitle] = useState(jobTitleParam || '');
	const [companyCity, setCompanyCity] = useState(companyCityParam || '');
	const [companyState, setCompanyState] = useState(companyStateParam || '');
	const [companyCountry, setCompanyCountry] = useState(companyCountryParam || '');
	const [accountName, setAccountName] = useState(signupAccountName || '');
	const [employeeNumber, setEmployeeNumber] = useState(employeeNumberParam || '');
	const [companyDescription, setCompanyDescription] = useState(companyDescriptionParam || '');
	const [numberOfAccounts, setNumberOfAccounts] = useState(numberOfAccountsParam || '');
	const [agreedReceiveContent, setAgreedReceiveContent] = useState(false);
	const [doCreateExecuted, setCreateExecuted] = useState(false);
	const [dataCenter, setDataCenter] = useState(() => {
		if (!shouldShowDataCenterOptions) {
			return null;
		}
		return isEuTimeZone ? dataCenters.eu : dataCenters.us;
	});

	const prevActiveStep = usePrevious(activeStep);

	const generateAuthenticationCode = useCallback(
		async (userEmail, sendEmail = true) => {
			const data = await generateAuthenticationCodeApi({
				email: userEmail,
				utm_source: utmParams.utmSource,
				utm_campaign: utmParams.utmCampaign,
				utm_medium: utmParams.utmMedium,
				utm_term: utmParams.utmTerm,
				utm_content: utmParams.utmContent,
				original_path: utmParams.originalPath,
				edition,
				account_name: accountName,
				send_email: sendEmail,
				first_name: firstName,
				last_name: lastName,
				avatar_id: avatarId,
				job_title: jobTitle,
				company_city: companyCity,
				company_state: companyState,
				company_country: companyCountry,
				company_description: companyDescription,
				number_of_accounts: numberOfAccounts,
				password,
				employee_number: employeeNumber,
				oauth_issuer: oauthIssuer,
				email_hash: emailHash,
				dataCenter,
			});
			if (data.redirect) {
				window.location.href = data.redirect;
			}
			return data;
		},
		[
			accountName,
			firstName,
			lastName,
			avatarId,
			jobTitle,
			companyCity,
			companyState,
			companyCountry,
			companyDescription,
			numberOfAccounts,
			password,
			employeeNumber,
			oauthIssuer,
			dataCenter,
			emailHash,
		],
	);

	const doTrackContact = useCallback(
		async ({ isCompleted, serviceId } = {}) => {
			await doTrackContactDummy({
				trimmedEmail,
				employeeNumber,
				firstName,
				lastName,
				jobTitle,
				companyCity,
				companyState,
				companyCountry,
				activeStep,
				isCompleted,
				isSuccess,
				agreedReceiveContent,
				serviceId,
			});
		},
		[
			trimmedEmail,
			employeeNumber,
			firstName,
			lastName,
			jobTitle,
			companyCity,
			companyState,
			companyCountry,
			activeStep,
			isSuccess,
			agreedReceiveContent,
		],
	);

	const nextStep = useCallback(
		newStep => {
			setActiveStep(newStep !== undefined ? newStep : activeStep + 1);
		},
		[activeStep],
	);

	const showStepper = activeStep !== 0;

	useEffect(() => {
		if (activeStep && prevActiveStep !== activeStep) {
			doTrackContact();
		}
	}, [activeStep, doTrackContact, prevActiveStep]);

	useEffect(() => {
		trackExternal(activeStep, isSuccess);
	}, [activeStep, isSuccess]);

	useEffect(() => {
		if (profileImageUrl) {
			uploadToUploadcareFromUrl(profileImageUrl, setAvatarId);
		}
	}, [profileImageUrl]);

	const handleRedirect = useCallback(() => {
		history.push('/downloadApp');
	}, [history]);

	const onAccountCreated = useCallback(
		async newService => {
			track('Signup::Signals', 'Signals');

			setIsLoading(false);
			setIsSuccess(true);

			setTimeout(() => {
				if (isMobile()) {
					handleRedirect();
				} else {
					const domain = accountName.toLowerCase();
					const userToken = newService.user['app-token'];
					const baseUrl = isLocal()
						? 'https://localhost:8443'
						: `https://${window.location.host}/${currentSite}/${domain}`;
					window.location.href = `${baseUrl}/loginByToken.html?app-token=${encodeURIComponent(
						userToken,
					)}&route=${DEFAULT_ROUTE}`;
				}
			}, 500);
		},
		[accountName, handleRedirect, isMobile],
	);

	const onCreationFailed = useCallback(error => {
		setErrorMessage(error);
		setIsError(true);
		setIsLoading(false);
	}, []);

	const [creationStatus, setCreationStatus] = useState(null);
	useEffect(() => {
		if (creationStatus) {
			const { status, data } = creationStatus;
			switch (status) {
				case 'done':
					onAccountCreated(data);
					break;
				case 'failed':
					onCreationFailed(data);
					break;
				case 'user_exists':
					window.location.href = isLocal()
						? `https://localhost:8443/debug.html#/${DEFAULT_ROUTE}`
						: `https://${window.location.host}#/${DEFAULT_ROUTE}`;
					break;
				case 'in_progress':
					waitForProgress(data, trimmedEmail, signupCode, setCreationStatus);
					break;
				default:
					break;
			}
		}
	}, [trimmedEmail, creationStatus, onAccountCreated, onCreationFailed]);

	const doCreateAccount = useCallback(() => {
		const generateService = async params => {
			try {
				const creationResponse = await createAccount({
					...params,
					...utmParams,
					short_signup: isShortSignup,
				});
				if (creationResponse.service_id) {
					await doTrackContactDummy({
						trimmedEmail,
						employeeNumber,
						firstName,
						lastName,
						jobTitle,
						companyCity,
						companyState,
						companyCountry,
						activeStep: 4,
						isCompleted: true,
						agreedReceiveContent: false,
						serviceId: creationResponse.service_id,
					});
					const signUpMethod = oauthIssuer ? SIGNUP_METHOD_TYPES.SSO : SIGNUP_METHOD_TYPES.STANDARD;
					gaPushEvent({ stepIndex: null, isSuccess: true, isFreeTrial: true, signUpMethod });
					onAccountCreated(creationResponse);
				} else {
					setCreationStatus(creationResponse);
				}
			} catch (error) {
				onCreationFailed(get(error, 'response.data.error'));
			}
		};

		const params = {
			code: signupCode,
			name: accountName.toLowerCase(),
			timezone: new Date().toString().match(/([-\+][0-9]+)\s/)[1], //i.e: +0400
			username: trimmedEmail,
			firstName,
			lastName,
			password,
			edition,
			title: jobTitle,
			company_city: companyCity,
			company_state: companyState,
			company_country: companyCountry,
			location: chosenDataCenter,
			companyDescription,
			numberOfAccounts,
			employeeNumber,
			oauthIssuer,
		};
		if (oauthIssuer && avatarId.length) {
			params.avatarId = avatarId;
		}
		setIsLoading(true);
		generateService(params);
	}, [
		accountName,
		trimmedEmail,
		firstName,
		lastName,
		password,
		jobTitle,
		companyCity,
		companyState,
		companyCountry,
		companyDescription,
		numberOfAccounts,
		employeeNumber,
		oauthIssuer,
		avatarId,
		onAccountCreated,
		onCreationFailed,
	]);

	const onResendLink = useCallback(() => {
		generateAuthenticationCode(trimmedEmail);
	}, [trimmedEmail, generateAuthenticationCode]);

	function getStepContent(step) {
		switch (step) {
			case 'Create Account':
				return (
					<CreateAccount
						handleNext={nextStep}
						email={trimmedEmail}
						setEmail={setEmail}
						password={password}
						setPassword={setPassword}
						accountName={accountName}
						setAccountName={setAccountName}
						isLoading={isLoading}
						setIsLoading={setIsLoading}
						key="Create Account"
						title="Get started for free"
						isActiveStep={activeStep === STEPS.indexOf(step)}
						setFirstName={setFirstName}
						setLastName={setLastName}
						setForceUpdateHeight={setForceUpdateHeight}
						oauthIssuer={oauthIssuer}
						setOauthIssuer={setOauthIssuer}
						setProfileImageUrl={setProfileImageUrl}
						setEmailHash={setEmailHash}
					/>
				);
			case 'Check your email':
				return (
					<CheckYourEmail isActiveStep={activeStep === STEPS.indexOf(step)} email={trimmedEmail} />
				);
			case 'Set user details':
				return (
					<SetUserDetails
						handleNext={nextStep}
						firstName={firstName}
						setFirstName={setFirstName}
						lastName={lastName}
						setLastName={setLastName}
						jobTitle={jobTitle}
						companyCity={companyCity}
						companyState={companyState}
						companyCountry={companyCountry}
						setJobTitle={setJobTitle}
						setCompanyCity={setCompanyCity}
						setCompanyState={setCompanyState}
						setCompanyCountry={setCompanyCountry}
						key="Set user details"
						title="Tell us about yourself"
						isActiveStep={activeStep === STEPS.indexOf(step)}
					/>
				);
			case 'Account settings':
				return (
					<AccountSettings
						handleNext={nextStep}
						accountName={accountName}
						setAccountName={setAccountName}
						employeeNumber={employeeNumber}
						setEmployeeNumber={setEmployeeNumber}
						companyDescription={companyDescription}
						setCompanyDescription={setCompanyDescription}
						numberOfAccounts={numberOfAccounts}
						setNumberOfAccounts={setNumberOfAccounts}
						agreedReceiveContent={agreedReceiveContent}
						setAgreedReceiveContent={setAgreedReceiveContent}
						setIsLoading={setIsLoading}
						isLoading={isLoading}
						key="Account settings"
						title="Just a few more details"
						isActiveStep={activeStep === STEPS.indexOf(step)}
						setForceUpdateHeight={setForceUpdateHeight}
						isSignInWithOauth={!!oauthIssuer}
						generateAuthenticationCode={generateAuthenticationCode}
						username={trimmedEmail}
						dataCenter={dataCenter}
						setDataCenter={setDataCenter}
						shouldShowDataCenterOptions={shouldShowDataCenterOptions}
					/>
				);
			default:
				throw new Error('Unknown step');
		}
	}

	useEffect(() => {
		if (signupCode) {
			doCreateAccount();
			setCreateExecuted(true);
		}
	}, [doCreateAccount]);

	const windowSize = useWindowSize();

	const [utmImage, setUtmImage] = useState('');
	const [isTestingBg, setIsTestingBg] = useState(!!utmParams.utmContent);
	useEffect(() => {
		const checkIfUtmImageExists = async fileId => {
			const doesFileExist = await isFileExists(fileId);
			if (doesFileExist) {
				setUtmImage(fileId);
			}
			setIsTestingBg(false);
		};
		if (utmParams.utmContent) {
			checkIfUtmImageExists(utmParams.utmContent);
		}
	}, []);

	const modalStyle = useMemo(() => {
		if (utmImage) {
			return {
				backgroundImage: `url(${uploadcareUrl(utmImage, windowSize.width)})`,
			};
		}
		if (isTestingBg) {
			return {};
		}
		return {
			backgroundImage: `url(${uploadcareUrl(
				BACKGROUND_UPLOADCARE_OPTIONS[backGroundPicture] || DEFAULT_SIGNUP_BACKGROUND_ID,
				windowSize.width,
			)})`,
		};
	}, [windowSize.width, utmImage, isTestingBg]);
	return (
		<MuiThemeProvider theme={totangoTheme}>
			<div className="signup-page">
				{!doCreateExecuted ? (
					<>
						{!isSuccess && !isError && (
							<Modal
								style={modalStyle}
								isOpen
								hideFooter
								hideHeader
								disableBackdropClick
								showHeaderCloseButton={false}
								className="signup-form-modal"
								message={
									<>
										<div className="left-signup-side-container">
											<div className="image-logo-wrapper">
												<img
													src="/img/logo/totango-dark.png"
													className="totango-name-logo-stepper"
													alt="Totango"
												/>
											</div>
											{showStepper && (
												<WizardNavigation
													stepsContent={STEPS}
													showCheckbox
													currentStep={activeStep}
													clickableSteps={false}
												/>
											)}
											<FormWizard
												className="form-wizard"
												activeStep={activeStep}
												steps={STEPS.map(step => getStepContent(step))}
												isLoading={isLoading}
												isElementVisible={showStepper}
												disableSwipe={true}
												setForceUpdateHeight={setForceUpdateHeight}
												forceUpdateHeight={forceUpdateHeight}
												isLinearProgressVisible={false}
											/>
										</div>
										<div className="right-signup-side-container">
											<MarketingIframe />
										</div>
									</>
								}
							/>
						)}
					</>
				) : (
					<>
						{isLoading && <CreateServiceLoader />}
						{isError && (
							<SignupError
								errorType={ERROR_TYPE_MAP[errorMessage]}
								onResendLink={onResendLink}
								signupEmail={signupEmail}
								signupDomain={toLower(accountName)}
							/>
						)}
					</>
				)}
			</div>
		</MuiThemeProvider>
	);
}
